import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Mail,
  Phone,
  Users,
  Calendar,
  ChevronRight,
  Home,
} from 'lucide-react';

import port__harcourt__office from '../assets/portharcourtGuys.jpg';
import port__enugu__office from '../assets/EnunguGuys.jpg';
import Herosection from '../components/shareCoponent/heroSection';

import '../styles/OurOffice.css';

const locationOurOffices = {
  lagos: {
    id: 'lagos',
    name: 'Lagos',
    title: 'Lagos Operations Hub',
    image: port__enugu__office,
    description:
      'Our Lagos office serves as the commercial capital headquarters, driving innovation and growth across West Africa. With our largest office of experts, we deliver cutting-edge solutions to enterprise clients.',
    location: 'Victoria Island, Lagos State',
    email: 'lagos@company.com',
    phone: '+234 (0) 1 234 5678',
    departments: 12,
    established: 2025,
    coordinates: { lat: 6.4281, lng: 3.4219 },
    stats: [
      { label: 'Office Members', value: '10+' },
      { label: 'Departments', value: '4' },
      { label: 'Years Active', value: '1' },
    ],
  },
  enugu: {
    id: 'enugu',
    name: 'Enugu',
    title: 'Enugu Technology Center',
    image: port__enugu__office,
    description:
      'Our Enugu office is a hub for technological advancement and research, focusing on developing new software and hardware solutions. Our dedicated office is passionate about pushing the boundaries of what’s possible.',
    location: '18 Marcus Garvey St, New Haven, Enugu 400102, Enugu, Nigeria',
    email: 'enugu@company.com',
    phone: '+234 (0) 42 123 4567',
    departments: 4,
    established: 2023,
    coordinates: { lat: 6.5244, lng: 7.4813 },
    stats: [
      { label: 'Office Members', value: '11' },
      { label: 'Departments', value: '8' },
      { label: 'Years Active', value: '2' },
    ],
  },
  'port-harcourt': {
    id: 'port-harcourt',
    name: 'Port Harcourt',
    title: 'Port Harcourt Energy Division',
    image: port__harcourt__office,
    description:
      'The Port Harcourt office specializes in providing innovative solutions for the energy and maritime sectors. We leverage local expertise to address the unique challenges of the region, ensuring sustainable growth and operational excellence.',
    location:
      '67 Tombia St, extension, New GRA, Port Harcourt 500001, Rivers, Nigeria',
    email: 'portharcourt@company.com',
    phone: '+234 (0) 84 123 4567',
    departments: 6,
    established: 2024,
    coordinates: { lat: 4.8156, lng: 7.0498 },
    stats: [
      { label: 'Office Members', value: '7' },
      { label: 'Departments', value: '3' },
      { label: 'Years Active', value: '2' },
    ],
  },
};

const OurOffice = () => {
  const [currentCity, setCurrentCity] = useState('lagos');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const ourOfficeData = locationOurOffices[currentCity];

  const handleCityChange = (cityId) => {
    if (cityId === currentCity) return;

    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentCity(cityId);
      setIsTransitioning(false);
    }, 300);
  };

  useEffect(() => {
    const path = window.location.hash || '#lagos';
    const city = path.replace('#', '') || 'lagos';
    if (locationOurOffices[city]) {
      setCurrentCity(city);
    }
  }, []);

  const updateURL = (cityId) => {
    window.history.pushState({}, '', `#${cityId}`);
  };

  return (
    <div className='our-office-container'>
      <div className='our-office-nav-container'>
        <div className='our-office-nav-content'>
          <div className='our-office-nav-wrapper'>
            <nav className='our-office-breadcrumb' aria-label="breadcrumb">
              <Home size={16} aria-hidden="true" />
              <ChevronRight size={16} aria-hidden="true" />
              <span>Our Office</span>
              <ChevronRight size={16} aria-hidden="true" />
              <span className='current-page'>{ourOfficeData.name}</span>
            </nav>

            <div className='our-office-tab-container' role="tablist">
              {Object.values(locationOurOffices).map((location) => (
                <button
                  key={location.id}
                  role="tab"
                  aria-selected={currentCity === location.id}
                  onClick={() => {
                    handleCityChange(location.id);
                    updateURL(location.id);
                  }}
                  className={`our-office-tab ${
                    currentCity === location.id ? 'active' : ''
                  }`}
                >
                  {location.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`our-office-content ${isTransitioning ? 'transitioning' : ''}`}>
        <Herosection title={ourOfficeData.title} />

        <main className='our-office-main-content'>
          <section className='our-office-section'>
            <div className='our-office-image-wrapper'>
              <img
                src={ourOfficeData.image}
                alt={`${ourOfficeData.name} office members`}
                className='our-office-city-image'
              />
            </div>

            <div className='our-office-description-container'>
              <h2 className='our-office-section-title'>
                About Our {ourOfficeData.name} office
              </h2>
              <p className='our-office-description'>{ourOfficeData.description}</p>

              <div className='our-office-stats-grid'>
                {ourOfficeData.stats.map((stat, index) => (
                  <div key={index} className='our-office-stat-card'>
                    <div className='our-office-stat-value'>{stat.value}</div>
                    <div className='our-office-stat-label'>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="our-office-contact-section">
            <h3 className="our-office-contact-title">
              Contact Our {ourOfficeData.name} Office
            </h3>

            <div className="our-office-contact-grid">
              {/* Location */}
              <div className="our-office-contact-item">
                <div className="our-office-contact-icon" aria-hidden="true">
                  <MapPin size={24} />
                </div>
                <div>
                  <h4 className="our-office-contact-label">Location</h4>
                  <p className="our-office-contact-value">{ourOfficeData.location}</p>
                </div>
              </div>

              {/* Email */}
              <div className="our-office-contact-item">
                <div className="our-office-contact-icon" aria-hidden="true">
                  <Mail size={24} />
                </div>
                <div>
                  <h4 className="our-office-contact-label">Email</h4>
                  <a
                    href={`mailto:${ourOfficeData.email}`}
                    className="our-office-contact-link"
                  >
                    {ourOfficeData.email}
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="our-office-contact-item">
                <div className="our-office-contact-icon" aria-hidden="true">
                  <Phone size={24} />
                </div>
                <div>
                  <h4 className="our-office-contact-label">Phone</h4>
                  <a
                    href={`tel:${ourOfficeData.phone}`}
                    className="our-office-phone-link"
                  >
                    {ourOfficeData.phone}
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section className='our-office-map-section'>
            <div className='our-office-map-header'>
              <h3 className='our-office-map-title'>Find Us</h3>
              <p className='our-office-map-subtitle'>
                Visit our {ourOfficeData.name} office at {ourOfficeData.location}
              </p>
            </div>
            <div className='our-office-map-container'>
              <iframe
                src={`https://maps.google.com/maps?q=${ourOfficeData.coordinates.lat},${ourOfficeData.coordinates.lng}&hl=en&z=14&output=embed`}
                width='100%'
                height='450'
                style={{ border: 0 }}
                allowFullScreen=''
                loading='lazy'
                referrerPolicy='no-referrer-when-downgrade'
                title={`${ourOfficeData.name} Office Location`}
              ></iframe>

              <div className='our-office-map-overlay'>
                <h4 className='our-office-overlay-title'>{ourOfficeData.title}</h4>
                <div className='our-office-overlay-item'>
                  <MapPin size={16} />
                  {ourOfficeData.location}
                </div>
                <div className='our-office-overlay-item'>
                  <Users size={16} />
                  {ourOfficeData.stats[0].value} office members
                </div>
                <div className='our-office-overlay-item'>
                  <Calendar size={16} />
                  Est. {ourOfficeData.established}
                </div>
              </div>
            </div>
          </section>

          <section className='our-office-cta-section'>
            <div className='our-office-cta-content'>
              <h2 className='our-office-cta-title'>
                Ready to Work with Our {ourOfficeData.name} office?
              </h2>
              <p className='our-office-cta-subtitle'>
                Get in touch to discuss how we can help your business grow.
              </p>
              <div className='our-office-cta-buttons'>
                <a
                  href={`mailto:${ourOfficeData.email}`}
                  className='our-office-cta-primary'
                >
                  <Mail size={18} style={{ marginRight: '8px' }} />
                  Send Email
                </a>
                <a
                  href={`tel:${ourOfficeData.phone}`}
                  className='our-office-cta-secondary'
                >
                  <Phone size={18} style={{ marginRight: '8px' }} />
                  Call Now
                </a>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default OurOffice;